/**
 * Aspect — Interactive doors (open/close with F or click, brown panels, collision gaps)
 * --------------------------------------------------------------
 * Spawns door leaves for exterior openings and interior partition walls. Doors
 * rotate on a hinge; when open they remove collision from the wall span so the
 * player can walk into the next room.
 */

import * as pc from "playcanvas";
import { paramOnWall as geomParamOnWall, wallLength, wallTangentAt, wallPointAt } from "../scene/wall_geometry.js";
import { doorPrice } from "../scene/structure_pricing.js";
import { hasOpeningAsset, initOpeningAssets, instantiateOpening, OPENING_NATIVE } from "../scene/opening_assets.js";

const DOOR_COLOR = new pc.Color(0.45, 0.28, 0.12);
const OPEN_SPEED = 220;
const INTERACT_RANGE = 3.2;
const AUTO_OPEN_RANGE = 2.2; // doors swing open automatically as the player nears
// Openings wider than this are rendered as a DOUBLE door (two single-sized leaves
// hinged on opposite jambs) instead of one oversized slab.
const DOUBLE_DOOR_MIN = 1.2;
const DOOR_MAX_W = 1.8;

function isDoorOpening(op) {
  return Boolean(op) && String(op.type ?? "").toLowerCase().includes("door");
}

/** Normalize [x,y,z] or 2D [x,z] opening centers so leaves never spawn at NaN. */
function openingWorldCenter(op, floorY = 0) {
  const c = op?.center;
  if (!Array.isArray(c) || c.length < 2) return null;
  const h = Number(op.height) || 2.1;
  if (c.length >= 3 && Number.isFinite(Number(c[2]))) {
    const y = Number(c[1]);
    return [Number(c[0]) || 0, Number.isFinite(y) ? y : floorY + h / 2, Number(c[2]) || 0];
  }
  return [Number(c[0]) || 0, floorY + h / 2, Number(c[1]) || 0];
}

function findWallForOpening(room, op) {
  const walls = room?.walls ?? [];
  const byId = walls.find((w) => w.id === op.wall);
  if (byId) return byId;
  const center = openingWorldCenter(op, room?.floorY ?? 0);
  const cx = center?.[0];
  const cz = center?.[2];
  if (!Number.isFinite(cx) || !Number.isFinite(cz)) return null;
  let best = null;
  let bestD = Infinity;
  for (const w of walls) {
    const t = geomParamOnWall(w, cx, cz);
    const [mx, mz] = wallPointAt(w, t);
    const d = Math.hypot(cx - mx, cz - mz);
    if (d < bestD) {
      bestD = d;
      best = w;
    }
  }
  return bestD < 0.8 ? best : null;
}

function worldAABB(entity) {
  const aabb = new pc.BoundingBox();
  let ok = false;
  for (const render of entity.findComponents("render")) {
    for (const mi of render.meshInstances) {
      if (!ok) {
        aabb.copy(mi.aabb);
        ok = true;
      } else {
        aabb.add(mi.aabb);
      }
    }
  }
  return ok ? aabb : null;
}

function rayAABB(origin, dir, aabb) {
  const min = aabb.getMin();
  const max = aabb.getMax();
  let tmin = -Infinity;
  let tmax = Infinity;
  for (const axis of ["x", "y", "z"]) {
    const o = origin[axis];
    const d = dir[axis];
    if (Math.abs(d) < 1e-9) {
      if (o < min[axis] || o > max[axis]) return null;
    } else {
      let t1 = (min[axis] - o) / d;
      let t2 = (max[axis] - o) / d;
      if (t1 > t2) [t1, t2] = [t2, t1];
      tmin = Math.max(tmin, t1);
      tmax = Math.min(tmax, t2);
      if (tmin > tmax) return null;
    }
  }
  return tmax >= 0 ? (tmin >= 0 ? tmin : 0) : null;
}

function screenRay(camera, x, y) {
  const cam = camera.camera;
  const near = cam.screenToWorld(x, y, cam.nearClip);
  const far = cam.screenToWorld(x, y, cam.farClip);
  const dir = far.clone().sub(near).normalize();
  return { origin: near, dir };
}

function doorMaterial() {
  const m = new pc.StandardMaterial();
  m.diffuse = DOOR_COLOR;
  m.gloss = 0.25;
  m.metalness = 0.05;
  m.update();
  return m;
}

function clamp01(t) {
  return Math.max(0, Math.min(1, t));
}

/** Derive a plausible product entry for a door from its size. */
function doorCatalog(w, h) {
  return {
    name: "Interior Door",
    brand: "Apartment Modern",
    material: "Wood · glass",
    price: doorPrice(w, h),
  };
}

export class DoorSystem {
  /**
   * @param {import('playcanvas').AppBase} app
   * @param {pc.Entity} camera
   * @param {import('../scene_manager.js').SceneManager} scene
   */
  constructor(app, camera, scene) {
    this.app = app;
    this.camera = camera;
    this.scene = scene;
    this.collision = scene.wallCollision;

    /** @type {Map<string, object>} */
    this.doors = new Map();
    this.doorMat = doorMaterial();
    this._targetDoorId = null;
    this._room = null;

    this._overhead = false;
    this.app.on("cam:mode-changed", ({ mode }) => {
      this._overhead = mode === "overhead";
    });

    this.app.on("scene:room-loaded", (room) => this._onRoom(room));
    this.app.on("update", (dt) => this._animate(dt));
    this.app.on("look:target-changed", ({ kind, id }) => {
      this._targetDoorId = kind === "door" ? id : null;
    });

    this.app.keyboard?.on(pc.EVENT_KEYDOWN, (e) => {
      if (e.key === pc.KEY_F) this._tryToggle();
    });

    // Prefetch Unity door GLB; re-spawn leaves once it arrives so new tours
    // never sit as empty plaster cutouts.
    if (scene?.registry) {
      initOpeningAssets(scene.registry).then(() => {
        if (this._room) this._onRoom(this._room);
      });
    }
  }

  _onRoom(room) {
    for (const [, d] of this.doors) d.entity?.parent?.destroy?.();
    this.doors.clear();
    this._room = room;
    if (!this.scene.roomRoot || !room) return;

    // Perimeter AND interior doorways are real openings now (room_factory emits
    // both), so a single pass spawns every door leaf that lines up with a gap.
    let n = 0;
    for (const op of room.openings ?? []) {
      if (!isDoorOpening(op)) continue;
      try {
        this._spawnDoor(op.id ?? `door_${++n}`, op, room);
      } catch (err) {
        console.warn("[doors] failed to spawn", op.id ?? n, err);
      }
    }

    this._rebuildCollision(room);
  }

  /** Replace one door leaf after a plan-editor move (no full room reload). */
  patchOpening(op) {
    if (!op?.id || !isDoorOpening(op)) return;
    this.removeOpening(op.id);
    const room = this._room ?? this.scene?.room;
    if (!room) return;
    this._spawnDoor(op.id, op, room);
    this._rebuildCollision(room);
  }

  /** Remove a door leaf from the scene. */
  removeOpening(id) {
    const d = this.doors.get(id);
    if (!d) return;
    d.entity?.parent?.destroy?.();
    this.doors.delete(id);
    this._rebuildCollision(this._room ?? this.scene?.room);
  }

  /** Drop all door leaves without rebuilding (structure stripped). */
  clearAll() {
    for (const [, d] of this.doors) d.entity?.parent?.destroy?.();
    this.doors.clear();
    this._rebuildCollision(this._room ?? this.scene?.room);
  }

  _spawnDoor(id, op, room) {
    const wall = findWallForOpening(room, op);
    if (!wall) return;

    const floorY = room.floorY ?? 0;
    const center = openingWorldCenter(op, floorY);
    if (!center) return;

    const len = wallLength(wall) || 1;
    const t = geomParamOnWall(wall, center[0], center[2]);
    const tan = wallTangentAt(wall, t);
    const dir = [tan[0], tan[1]];
    const cx = center[0];
    const cy = center[1];
    const cz = center[2];
    const w = Math.min(Number(op.width) || 0.9, DOOR_MAX_W, Math.max(0.6, len - 0.15));
    const h = op.height ?? 2.1;
    const thick = 0.06;

    // The wall's inward normal points into the room. `swing` selects which face
    // the leaf opens toward: "in" = inward normal (default), "out" = the opposite
    // side. The chosen normal drives the hinge rotation direction.
    let nx = wall.normal?.[0] ?? 0;
    let nz = wall.normal?.[1] ?? 1;
    const nlen = Math.hypot(nx, nz) || 1;
    nx /= nlen;
    nz /= nlen;
    const swing = op.swing === "out" ? "out" : "in";
    const snx = swing === "out" ? -nx : nx;
    const snz = swing === "out" ? -nz : nz;
    // `hinge` picks the jamb the leaf pivots on: "left" = the wall's start jamb
    // (cx - dir·w/2), "right" = the end jamb. Default "left".
    const hingeSide = op.hinge === "right" ? "right" : "left";

    const root = new pc.Entity(`door-${id}`);
    this.scene.roomRoot.addChild(root);

    // Each leaf is hinged at ONE jamb and offset by +half-leaf-width along its
    // local X so it swings around the edge (never its own middle). A wide opening
    // becomes TWO single-sized leaves meeting in the middle (a double door).
    const leaves = [];
    const makeLeaf = (ldir, hingeX, hingeZ, leafW) => {
      const baseYaw = (Math.atan2(-ldir[1], ldir[0]) * 180) / Math.PI;
      const hingeSign = ldir[1] * snx - ldir[0] * snz > 0 ? 1 : -1;
      const hinge = new pc.Entity(`door-hinge-${id}-${leaves.length}`);
      hinge.setLocalPosition(hingeX, cy, hingeZ);
      hinge.setLocalEulerAngles(0, baseYaw, 0);
      const panel = new pc.Entity(`door-panel-${id}-${leaves.length}`);
      panel._aspectDoorId = id;
      panel._aspectInteractable = true;

      const mesh = hasOpeningAsset("door") ? instantiateOpening("door") : null;
      const hasRender = Boolean(mesh?.findComponents?.("render")?.length);
      if (mesh && hasRender) {
        // GLB width is along local Z; +90° Y maps it onto hinge local +X.
        const nat = OPENING_NATIVE.door;
        mesh.setLocalEulerAngles(0, 90, 0);
        mesh.setLocalScale(1, h / nat.height, leafW / nat.width);
        // Origin is floor-center of the leaf; hinge sits at vertical mid.
        mesh.setLocalPosition(leafW / 2, -h / 2, 0);
        mesh._aspectDoorId = id;
        mesh._aspectInteractable = true;
        panel.addChild(mesh);
      } else {
        panel.addComponent("render", { type: "box", material: this.doorMat, castShadows: true });
        panel.setLocalScale(leafW, h, thick);
        panel.setLocalPosition(leafW / 2, 0, 0);
      }

      hinge.addChild(panel);
      root.addChild(hinge);
      leaves.push({ hinge, panel, baseYaw, hingeSign });
    };

    const double = w > DOUBLE_DOOR_MIN;
    if (double) {
      const leafW = w / 2;
      // Left leaf hinged at jamb A pointing toward centre (+dir); right leaf
      // hinged at jamb B pointing toward centre (-dir). Both swing inward.
      makeLeaf(dir, cx - dir[0] * (w / 2), cz - dir[1] * (w / 2), leafW);
      makeLeaf([-dir[0], -dir[1]], cx + dir[0] * (w / 2), cz + dir[1] * (w / 2), leafW);
    } else if (hingeSide === "right") {
      // Hinge at the end jamb; the leaf points back toward the start jamb.
      makeLeaf([-dir[0], -dir[1]], cx + dir[0] * (w / 2), cz + dir[1] * (w / 2), w);
    } else {
      // Hinge at the start jamb; the leaf points toward the end jamb.
      makeLeaf(dir, cx - dir[0] * (w / 2), cz - dir[1] * (w / 2), w);
    }

    const tCenter = this._paramOnWall(wall, cx, cz);
    const halfT = w / 2 / len;

    this.doors.set(id, {
      id,
      entity: leaves[0].hinge,
      panel: leaves[0].panel,
      leaves,
      center: new pc.Vec3(cx, cy, cz),
      wallId: wall.id,
      wall,
      width: w,
      height: h,
      double,
      open: false,
      manual: false,
      angle: 0,
      targetAngle: 0,
      t0: clamp01(tCenter - halfT),
      t1: clamp01(tCenter + halfT),
      ...doorCatalog(w, h),
    });
  }

  /** Spec data (name/brand/price/material) for the hovered-door info card. */
  meta(id) {
    const d = this.doors.get(id);
    if (!d) return null;
    return {
      entity: d.panel,
      name: d.name,
      brand: d.brand,
      material: d.material,
      price: d.price,
      width: d.width,
      height: d.height,
    };
  }

  _paramOnWall(wall, px, pz) {
    return geomParamOnWall(wall, px, pz);
  }

  /** Every doorway is a permanent gap in the walls so the player can roam freely. */
  gapsForWall(wallId) {
    const gaps = [];
    for (const d of this.doors.values()) {
      if (d.wallId !== wallId) continue;
      gaps.push({ t0: d.t0, t1: d.t1 });
    }
    if (gaps.length > 0) return gaps;

    const room = this._room ?? this.scene?.room;
    const wall = room?.walls?.find((w) => w.id === wallId);
    if (!wall) return gaps;

    for (const op of room.openings ?? []) {
      if (!isDoorOpening(op) || (op.wall && op.wall !== wallId)) continue;
      const len = wallLength(wall) || 1;
      const oc = openingWorldCenter(op, room.floorY ?? 0);
      if (!oc) continue;
      const tCenter = this._paramOnWall(wall, oc[0], oc[2]);
      const halfT = (op.width ?? 0.9) / 2 / len;
      gaps.push({ t0: clamp01(tCenter - halfT), t1: clamp01(tCenter + halfT) });
    }
    return gaps;
  }

  _rebuildCollision(room) {
    this.collision?.rebuild(room ?? this.scene.room, this);
  }

  _tryToggle() {
    if (this._targetDoorId) this.toggleDoor(this._targetDoorId);
  }

  /** Raycast doors at a screen point. Returns { id, t } for the nearest hit. */
  raycastDoor(x, y) {
    const { origin, dir } = screenRay(this.camera, x, y);
    let bestId = null;
    let bestT = Infinity;
    for (const hit of this.interactables()) {
      const aabb = worldAABB(hit.entity);
      if (!aabb) continue;
      const t = rayAABB(origin, dir, aabb);
      if (t !== null && t < bestT) {
        bestT = t;
        bestId = hit.id;
      }
    }
    return bestId !== null ? { id: bestId, t: bestT } : null;
  }

  /** Toggle a door if the player is close enough. Returns true on success. */
  toggleDoor(id) {
    const door = this.doors.get(id);
    if (!door) return false;

    const eye = this.camera.getPosition();
    const pos = door.center ?? door.entity.getPosition();
    if (Math.hypot(eye.x - pos.x, eye.z - pos.z) > INTERACT_RANGE) return false;

    door.open = !door.open;
    door.manual = true; // hold this state until the player walks away (auto re-arms)
    door.targetAngle = door.open ? 90 : 0;
    this.app.fire("door:toggled", { id, open: door.open });
    return true;
  }

  _animate(dt) {
    const eye = this.camera.getPosition();
    for (const d of this.doors.values()) {
      // Proximity auto-open: doors open as the player approaches and close once
      // they leave. A manual F/click near a door overrides this until the player
      // walks away (then the door re-arms for auto behaviour).
      const pos = d.center ?? d.entity.getPosition();
      // Proximity auto-open is a first-person convenience only — in overhead/
      // scene-view the flying camera would otherwise pop every door it passes.
      const near = !this._overhead && Math.hypot(eye.x - pos.x, eye.z - pos.z) < AUTO_OPEN_RANGE;
      if (d.manual && !near) d.manual = false;
      if (!d.manual) {
        const want = near;
        if (want !== d.open) {
          d.open = want;
          d.targetAngle = want ? 90 : 0;
        }
      }

      if (Math.abs(d.angle - d.targetAngle) < 0.5) {
        d.angle = d.targetAngle;
      } else {
        const step = OPEN_SPEED * dt;
        d.angle += d.targetAngle > d.angle ? step : -step;
        if (Math.abs(d.angle - d.targetAngle) < step) d.angle = d.targetAngle;
      }
      // Rotate each leaf's HINGE (edge pivot) by the shared magnitude in its own
      // swing direction, so double-door leaves open outward from the centre.
      for (const leaf of d.leaves) {
        leaf.hinge.setLocalEulerAngles(0, leaf.baseYaw + d.angle * leaf.hingeSign, 0);
      }
    }
  }

  /** @returns {{ id:string, kind:'door', entity:pc.Entity }[]} */
  interactables() {
    const out = [];
    for (const d of this.doors.values()) {
      for (const leaf of d.leaves) out.push({ id: d.id, kind: "door", entity: leaf.panel });
    }
    return out;
  }

  destroy() {
    this.doorMat?.destroy();
    this.doors.clear();
  }
}

export default DoorSystem;
